import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { Plus, Edit2, Trash2, DollarSign, Clock, X, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_CATEGORIES = [
  { id: "barber", name: "Barber", icon: "✂️" },
  { id: "hair_stylist", name: "Hair Stylist", icon: "💇" },
  { id: "consultant", name: "Consultant", icon: "💼" },
  { id: "mechanic", name: "Mechanic", icon: "🔧" },
  { id: "spiritual", name: "Spiritual Services", icon: "🙏" },
  { id: "tutor", name: "Tutor", icon: "📚" },
  { id: "vendor", name: "Local Vendor", icon: "🏪" },
  { id: "health", name: "Health & Wellness", icon: "💆" },
  { id: "beauty", name: "Beauty", icon: "💄" },
  { id: "other", name: "Other", icon: "📋" },
];

// Service Card Component
const ServiceCard = ({ service, onEdit, onDelete, isOwner, onBook, lang }) => {
  const category = SERVICE_CATEGORIES.find(c => c.id === service.category) || SERVICE_CATEGORIES[9];
  const tr = t(lang);
  
  return (
    <div 
      className="bg-card border rounded-xl overflow-hidden hover:shadow-lg hover:border-pink-500/30 transition-all group"
      data-testid={`service-card-${service.id}`}
    >
      {/* Category Header */}
      <div className="bg-gradient-to-r from-pink-500/10 to-violet-500/10 px-4 py-2 flex items-center gap-2">
        <span className="text-xl">{category.icon}</span>
        <span className="text-xs font-medium text-muted-foreground">{category.name}</span>
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold">{service.name}</h3>
          {isOwner && (
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(service)}>
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(service.id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
        
        {service.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{service.description}</p>
        )}
        
        {/* Price & Duration */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>{service.duration_minutes} {tr('minutes')}</span>
          </div>
          <div className={cn(
            "text-lg font-bold",
            service.is_free ? "text-green-500" : "text-pink-500"
          )}>
            {service.is_free ? tr('free') : `$${service.price}`}
          </div>
        </div>
        
        {/* Book Button - Only for non-owners */}
        {!isOwner && onBook && (
          <Button 
            className="w-full rounded-full bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600"
            onClick={() => onBook(service)}
            data-testid={`book-btn-${service.id}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            {tr('bookAppointment')}
          </Button>
        )}
      </div>
    </div>
  );
};

// Service Form Modal
const ServiceFormModal = ({ open, onClose, service, onSave, lang }) => {
  const tr = t(lang);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: 0,
    is_free: false,
    duration_minutes: 60,
    category: 'other'
  });
  
  useEffect(() => {
    if (service) {
      setForm({
        name: service.name || '',
        description: service.description || '',
        price: service.price || 0,
        is_free: service.is_free || false,
        duration_minutes: service.duration_minutes || 60,
        category: service.category || 'other'
      });
    } else {
      setForm({
        name: '',
        description: '',
        price: 0,
        is_free: false,
        duration_minutes: 60,
        category: 'other'
      });
    }
  }, [service, open]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(tr('fillAllFields'));
      return;
    }
    setLoading(true);
    await onSave(form, service?.id);
    setLoading(false);
  };
  
  // Labels based on language
  const labels = {
    editService: lang === 'ht' ? 'Modifye Sèvis' : lang === 'fr' ? 'Modifier Service' : lang === 'es' ? 'Editar Servicio' : 'Edit Service',
    newService: lang === 'ht' ? 'Nouvo Sèvis' : lang === 'fr' ? 'Nouveau Service' : lang === 'es' ? 'Nuevo Servicio' : 'New Service',
    serviceName: lang === 'ht' ? 'Non Sèvis' : lang === 'fr' ? 'Nom du Service' : lang === 'es' ? 'Nombre del Servicio' : 'Service Name',
    description: lang === 'ht' ? 'Deskripsyon' : lang === 'fr' ? 'Description' : lang === 'es' ? 'Descripción' : 'Description',
    category: lang === 'ht' ? 'Kategori' : lang === 'fr' ? 'Catégorie' : lang === 'es' ? 'Categoría' : 'Category',
    duration: lang === 'ht' ? 'Dire (minit)' : lang === 'fr' ? 'Durée (min)' : lang === 'es' ? 'Duración (min)' : 'Duration (min)',
    price: lang === 'ht' ? 'Pri ($)' : lang === 'fr' ? 'Prix ($)' : lang === 'es' ? 'Precio ($)' : 'Price ($)',
    freeService: lang === 'ht' ? 'Sèvis Gratis' : lang === 'fr' ? 'Service Gratuit' : lang === 'es' ? 'Servicio Gratis' : 'Free Service',
    update: lang === 'ht' ? 'Mete Ajou' : lang === 'fr' ? 'Mettre à jour' : lang === 'es' ? 'Actualizar' : 'Update',
    create: lang === 'ht' ? 'Kreye' : lang === 'fr' ? 'Créer' : lang === 'es' ? 'Crear' : 'Create',
    hour: lang === 'ht' ? 'èdtan' : lang === 'fr' ? 'heure' : lang === 'es' ? 'hora' : 'hour',
    hours: lang === 'ht' ? 'èdtan' : lang === 'fr' ? 'heures' : lang === 'es' ? 'horas' : 'hours',
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{service ? labels.editService : labels.newService}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{labels.serviceName} *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="Ex: Haircut"
              data-testid="service-name-input"
            />
          </div>
          
          <div>
            <Label>{labels.description}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              placeholder={lang === 'ht' ? 'Dekri sèvis ou a...' : 'Describe your service...'}
              rows={3}
            />
          </div>
          
          <div>
            <Label>{labels.category}</Label>
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{labels.duration}</Label>
              <Select 
                value={form.duration_minutes.toString()} 
                onValueChange={(v) => setForm({...form, duration_minutes: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="45">45 min</SelectItem>
                  <SelectItem value="60">1 {labels.hour}</SelectItem>
                  <SelectItem value="90">1.5 {labels.hours}</SelectItem>
                  <SelectItem value="120">2 {labels.hours}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{labels.price}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})}
                disabled={form.is_free}
                data-testid="service-price-input"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <Label className="cursor-pointer">{labels.freeService}</Label>
            <Switch
              checked={form.is_free}
              onCheckedChange={(v) => setForm({...form, is_free: v, price: v ? 0 : form.price})}
            />
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              {tr('cancel')}
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (service ? labels.update : labels.create)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Services Section Component
export const ServicesSection = ({ userId, isOwner }) => {
  const { token, language } = useAuth();
  const tr = t(language);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  
  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (userId) {
      fetchServices();
    }
  }, [userId, token]);
  
  // Labels based on language
  const labels = {
    serviceUpdated: language === 'ht' ? 'Sèvis mete ajou!' : language === 'fr' ? 'Service mis à jour!' : language === 'es' ? '¡Servicio actualizado!' : 'Service updated!',
    serviceCreated: language === 'ht' ? 'Sèvis kreye!' : language === 'fr' ? 'Service créé!' : language === 'es' ? '¡Servicio creado!' : 'Service created!',
    serviceDeleted: language === 'ht' ? 'Sèvis efase!' : language === 'fr' ? 'Service supprimé!' : language === 'es' ? '¡Servicio eliminado!' : 'Service deleted!',
    confirmDelete: language === 'ht' ? 'Ou sèten ou vle efase sèvis sa a?' : language === 'fr' ? 'Êtes-vous sûr de vouloir supprimer ce service?' : language === 'es' ? '¿Está seguro de que desea eliminar este servicio?' : 'Are you sure you want to delete this service?',
    servicesAndAppointments: language === 'ht' ? 'Sèvis & Randevou' : language === 'fr' ? 'Services & Rendez-vous' : language === 'es' ? 'Servicios & Citas' : 'Services & Appointments',
    addService: language === 'ht' ? 'Ajoute Sèvis' : language === 'fr' ? 'Ajouter Service' : language === 'es' ? 'Agregar Servicio' : 'Add Service',
    addFirstService: language === 'ht' ? 'Ajoute premye sèvis ou' : language === 'fr' ? 'Ajoutez votre premier service' : language === 'es' ? 'Agrega tu primer servicio' : 'Add your first service',
    error: language === 'ht' ? 'Erè' : 'Error',
  };
  
  const handleSave = async (form, serviceId) => {
    try {
      if (serviceId) {
        await axios.put(`${API}/services/${serviceId}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(labels.serviceUpdated);
      } else {
        await axios.post(`${API}/services`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(labels.serviceCreated);
      }
      fetchServices();
      setShowForm(false);
      setEditingService(null);
    } catch (error) {
      toast.error(labels.error + ': ' + (error.response?.data?.detail || ''));
    }
  };
  
  const handleDelete = async (serviceId) => {
    if (!window.confirm(labels.confirmDelete)) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(labels.serviceDeleted);
      fetchServices();
    } catch (error) {
      toast.error(labels.error);
    }
  };
  
  const handleEdit = (service) => {
    setEditingService(service);
    setShowForm(true);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
      </div>
    );
  }
  
  if (services.length === 0 && !isOwner) {
    return null;
  }
  
  return (
    <div className="mt-6" data-testid="services-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">{labels.servicesAndAppointments}</h3>
        {isOwner && (
          <Button 
            size="sm" 
            onClick={() => { setEditingService(null); setShowForm(true); }}
            className="rounded-full"
            data-testid="add-service-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            {labels.addService}
          </Button>
        )}
      </div>
      
      {services.length === 0 ? (
        <div className="text-center py-8 bg-secondary/30 rounded-xl">
          <p className="text-muted-foreground">{tr('noAppointments')}</p>
          {isOwner && (
            <Button 
              variant="link" 
              onClick={() => setShowForm(true)}
              className="text-pink-500"
            >
              {labels.addFirstService}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {services.map(service => (
            <ServiceCard 
              key={service.id}
              service={service}
              isOwner={isOwner}
              onEdit={handleEdit}
              onDelete={handleDelete}
              lang={language}
            />
          ))}
        </div>
      )}
      
      <ServiceFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditingService(null); }}
        service={editingService}
        onSave={handleSave}
        lang={language}
      />
    </div>
  );
};

export default ServicesSection;
